import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.js';
import { listPending, type PendingWrite } from './db.js';
import { flushQueue, setQueueEnabled, subscribeToQueue } from './queue.js';

/**
 * How often to retry while the browser claims to be online.
 *
 * `navigator.onLine` only knows whether an interface is up, so it says "online"
 * on a hotel wifi that has not been logged into yet. A slow heartbeat catches
 * the moment that actually starts working, without polling like a spinlock.
 */
const RETRY_INTERVAL_MS = 60_000;

export interface OfflineQueueState {
  enabled: boolean;
  online: boolean;
  pending: PendingWrite[];
  flush: () => void;
}

/**
 * Live view of the queue, and the thing that empties it.
 *
 * Mounted once near the root: the flush must keep happening while the user is
 * on any screen, not only the one that shows the banner.
 */
export function useOfflineQueue(): OfflineQueueState {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const enabled = user?.preferences.offlineQueue ?? false;
  const [pending, setPending] = useState<PendingWrite[]>([]);
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    setQueueEnabled(enabled);
  }, [enabled]);

  useEffect(() => subscribeToQueue(setPending), []);

  useEffect(() => {
    if (!enabled) return undefined;

    const attempt = async (): Promise<void> => {
      const before = (await listPending().catch(() => [])).length;
      if (before === 0) return;

      await flushQueue();

      // Anything that landed changed a day the screens are showing. Refetching
      // only when the count actually drops keeps a permanently stuck entry from
      // re-querying the whole app every minute.
      const after = (await listPending().catch(() => [])).length;
      if (after < before) await queryClient.invalidateQueries();
    };

    const goOnline = (): void => {
      setOnline(true);
      void attempt();
    };
    const goOffline = (): void => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const timer = window.setInterval(() => {
      if (navigator.onLine) void attempt();
    }, RETRY_INTERVAL_MS);

    void attempt();

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.clearInterval(timer);
    };
  }, [enabled, queryClient]);

  return {
    enabled,
    online,
    pending,
    flush: () => {
      void flushQueue().then(() => queryClient.invalidateQueries());
    },
  };
}
